import { SQUARE_APPLICATION_ID, SQUARE_LOCATION_ID } from '@/config';

import React from 'react';
import { ChargeVerifyBuyerDetails, payments as squarePayments } from '@square/web-sdk';

import { SessionContext } from '@/components/Context/Session';
import { createPaymentIntent, finalizeSquarePayment } from '@/services/api/payment';
import { PaymentChargeRequest, PaymentIntentResponse, PaymentResponseStatus, Pricing } from '@flyblack/common/domains';

interface PaymentSubmitOption extends Omit<PaymentChargeRequest, 'bankAccountId'> {
  paymentMethodId: string;
  price: Pricing;
}

export const useSquarePayment = () => {
  const { me, userCities } = React.useContext(SessionContext);

  const verifyBuyer = async (token, price) => {
    const verificationDetails: ChargeVerifyBuyerDetails = {
      billingContact: {
        givenName: me.firstName,
        familyName: me.lastName,
        city: userCities?.homeLocation.name,
        countryCode: userCities?.homeLocation.countryCode
      },
      amount: price.value.toFixed(2), // amount must be in cents
      currencyCode: price.currency, // 3 letter currency code
      intent: 'CHARGE'
    };

    const verificationResults = await squarePayments(SQUARE_APPLICATION_ID, SQUARE_LOCATION_ID).then((payments) =>
      payments.verifyBuyer(token, verificationDetails)
    );

    return verificationResults.token;
  };

  const submitPayment = ({ paymentMethodId, price, ...paymentChargeRequestParams }: PaymentSubmitOption) => {
    return new Promise((resolve, reject) => {
      return createPaymentIntent(paymentChargeRequestParams)
        .then(({ orderId, flightStatus }: PaymentIntentResponse) => {
          if (flightStatus !== PaymentResponseStatus.FreeFlight) {
            // Square buyer verification. Required in SCA Mandated Regions
            return verifyBuyer(paymentMethodId, price).then((verificationToken) => {
              // Finalize payment
              return finalizeSquarePayment({ orderId, sourceId: paymentMethodId, verificationToken })
                .then(() => {
                  return resolve(flightStatus);
                })
                .catch((error) => reject(error));
            });
          } else {
            return resolve(flightStatus);
          }
        })
        .catch((error) => reject(error));
    });
  };

  return { submitPayment };
};
